import Vue from 'vue';
import Vuex from 'vuex';
import {
	API_PATCH_PUBLIC_PROFILE_SELF,
	API_PATCH_USER_BADGES,
	API_GET_USER_GEAR,
	API_PATCH_USER_GEAR,
	API_CREATE_USER_GEAR,
	API_DELETE_USER_GEAR,
	API_PATCH_CONTACT,
	API_GET_PUBLIC_PROFILE,
	API_GET_MORE_MEMBERS,
	API_LIKE_PROFILE_OR_PHOTO,
	API_LIKE_PROFILE_OR_PHOTO_2,
	API_ENABLE_PUBLIC_PROFILE_SELF,
	API_REPORT_PROFILE_OR_PHOTO,
	API_GET_COMMUNITY_GALLERY,
	API_GET_PROFILE_OR_PHOTO_LIKES,
	API_GET_MEDIA_ITEM,
	API_GET_LEAN_PROFILE,
	API_GET_OEMBED_VIMEO_VIDEO,
	API_GET_OEMBED_TIKTOK_VIDEO,
	API_GET_OEMBED_YOUTUBE_VIDEO,
	API_TRIGGER_CONTACT_UPDATE,
} from '@/api-service.js';
import {SHUFFLE_ARRAY} from '@/helpers.js';
import {uploadPhoto} from '@/upload-service.js';
import DYNAMICS_ENTITIES from "@/PROFILE_CONSTANTS/DYNAMICS_ENTITIES";

function normalizeLikeId(id) {
	return id == null ? '' : String(id).trim().toLowerCase();
}

function isIdInLikeList(likes, ...ids) {
	const targets = new Set(ids.filter(Boolean).map(normalizeLikeId));
	return likes.some(like => targets.has(normalizeLikeId(like)));
}

Vue.use(Vuex);

const accountInfo = {
	state: {
		Sections: {
			General: {
				saveStatus: 'saving',
			},
			ContactInfo: {
				saveStatus: 'saving',
				sectionError: '',
			},
			AboutMe: {
				saveStatus: 'saving',
			},
			GearLocker: {
				saveStatus: 'saving',
			},
			GearItemDetails: {
				saveStatus: 'complete',
			},
			Retailers: {
				saveStatus: 'saving',
			},
		},
	},
	mutations: {
		setAccountSectionStatusComplete(state, sectionStatus) {
			if (sectionStatus) {
				state.Sections[sectionStatus.key].saveStatus =
					sectionStatus.saveStatus;
			} else {
				for (let section in state.Sections) {
					state.Sections[section].saveStatus = 'complete';
				}
			}
		},
		updateAccountSectionStatus(state, payload) {
			state.Sections[payload.sectionName].saveStatus = payload.saveStatus;
		},
		updateAccountSectionError(state, payload) {
			state.Sections[payload.sectionName].sectionError =
				payload.errorMessage;
		},
	},
	actions: {
		async saveSectionContactInfo({commit}, payload) {
			if (payload) {
				let contactFormData = new FormData();
				let sectionName = payload.sectionName;
				commit({
					type: 'updateAccountSectionStatus',
					sectionName: sectionName,
					saveStatus: 'saving',
				});
				if (payload.updatedFields) {
					let updatedField;
					for (let key in payload.updatedFields) {
						//replace null and undefined values with empty string
						updatedField = payload.updatedFields[key] === null || payload.updatedFields[key] === undefined ? '' : payload.updatedFields[key];
						contactFormData.append(key, updatedField);
					}
				} else {
					return;
				}
				try {
					let response = await API_PATCH_CONTACT(
						$cookies.get('au_guid'),
						contactFormData
					);
				} catch (error) {
					// Axios v1.x: Error response structure may differ
					// Defensive check for error.response and error.response.data
					let serverErrorMessage = 'An error occurred';
					if (error.response && error.response.data) {
						serverErrorMessage = error.response.data.message || error.response.data.error || serverErrorMessage;
					} else if (error.message) {
						serverErrorMessage = error.message;
					}
					commit({
						type: 'updateAccountSectionError',
						sectionName: sectionName,
						errorMessage: serverErrorMessage,
					});
				}
				commit({
					type: 'updateAccountSectionStatus',
					sectionName: sectionName,
					saveStatus: 'complete',
				});
			}
		},
		async triggerContactUpdate ({commit}) {
			console.debug('contact update called');
			try {
				const contactUpdateResponse = await API_TRIGGER_CONTACT_UPDATE();
				console.debug('contact updated');
				commit('updateContactInfo', contactUpdateResponse.response.data);
				console.debug('contact commited');
			} catch (err) {
				// Axios v1.x: Error response structure may differ
				// Defensive check for err.response and err.response.data
				const errorMessage = (err.response && err.response.data && err.response.data.message) 
					? err.response.data.message 
					: err.message || 'Failed to update contact';
				console.error(`Failed to update contact: ${errorMessage}`);
			}
		}
	},
};

const publicUserInfo = {
	namespaced: true,
	state: {
		publicUser: {},
		galleryMedia: [],
		isLiked: false,
		isLoading: true,
		isSelfProfile: false,
		profileErrorStatus: '',
		profileUserLikes: [],
	},
	mutations: {
		checkIsSelfProfile(state) {
			state.isSelfProfile = $cookies.get('au_profile_guid') === state.publicUser.profile.ppd_publicprofileid;
		},
		updateIsLoadingState(state, payload) {
			state.isLoading = payload;
		},
		setPublicUserData(state, payload) {
			state.publicUser = payload;
			state.isLoading = false;
		},
		setGalleryMedia(state, galleryPayload) {
			state.galleryMedia = galleryPayload;
			// state.isLoading = false;
		},
		resetGalleryMedia(state) {
			state.galleryMedia = [];
		},
		setProfileErrorStatus(state, message) {
			state.profileErrorStatus = message;
		},
		updateProfileLikesStatus(state, payload) {
			const stat = payload.stat || payload;
			const data = stat?.data || {};
			const currentLikes = data.currentLikes ?? [];

			state.profileUserLikes = currentLikes;

			if (data.newCount != null) {
				Vue.set(state.publicUser.profile, 'ppd_profilelikes', Number(data.newCount));
			}

			// Each successful like POST is a user toggle; currentLikes/newCount delta can desync on re-like
			state.isLiked = !state.isLiked;
		},
		setUserLikeState(state, payload) {
			state.profileUserLikes = payload;
			state.isLiked = isIdInLikeList(
				payload,
				state.publicUser.profile.ppd_publicprofileid
			);
		},
		updateProfileImageLikesStatus(state, payload) {
			state.profileUserLikes = payload.stat.data.currentLikes;
			let tempMedia = state.galleryMedia.map(imageObj => {
				if (imageObj.ppd_usermediaid === payload.content) {
					imageObj.ppd_photolikes = payload.stat.data.newCount;
					imageObj.isLiked = !imageObj.isLiked;
				}
				return imageObj;
			});
			Vue.set(state.galleryMedia, tempMedia);
		},
	},
	actions: {
		async getPublicUserData({dispatch, state, commit}, userName) {
			commit('updateIsLoadingState', true);
			commit('resetGalleryMedia');
			const userData = await API_GET_PUBLIC_PROFILE(userName);
			if (!userData.error) {
				commit('setProfileErrorStatus', '');
				//create tempvalue for user media
				let tempGallery = userData.usermedia;
				let userMediaGallery = [];
				delete userData.usermedia;
				commit('setPublicUserData', userData);
				let likerGUID = $cookies.get('au_profile_guid');
				await dispatch('getUserLikes', likerGUID);
				userMediaGallery = tempGallery.map(imageObj => {
					if (!imageObj.ppd_isprofile) {
						imageObj.isLiked = state.profileUserLikes.includes(imageObj.ppd_usermediaid);
						return imageObj;
					}
				});

				userMediaGallery = userMediaGallery.map( media => {
					if (media.ppd_mediatype !== DYNAMICS_ENTITIES.MEDIA_TYPES['Video']) {
						return media;
					}
					const video = {...media};
					video.ppd_videooptions = JSON.parse(video.ppd_videooptions);
					return video;
				});

				/*try {
					userMediaGallery = await Promise.all(userMediaGallery.map(async media => {
						if (media.ppd_mediatype !== DYNAMICS_ENTITIES.MEDIA_TYPES['Video']) {
							return media;
						}

						const video = {...media};
						video.ppd_videooptions = JSON.parse(video.ppd_videooptions);
						let oembedResponse;
						switch (video.ppd_videooptions.videoProvider) {
							case 'YouTube':
								oembedResponse = await API_GET_OEMBED_YOUTUBE_VIDEO(video.ppd_videourl);
								video.thumbnailUrl = oembedResponse.thumbnail_url;
								video.embedHtml = oembedResponse.html;
								return video;
							case 'TikTok':
								oembedResponse = await API_GET_OEMBED_VIMEO_VIDEO(video.ppd_videourl);
								video.thumbnailUrl = oembedResponse.thumbnail_url;
								video.embedHtml = oembedResponse.html;
								return video;
							case 'Vimeo':
								oembedResponse = await API_GET_OEMBED_TIKTOK_VIDEO(video.ppd_videourl);
								video.thumbnailUrl = oembedResponse.thumbnail_url;
								video.embedHtml = oembedResponse.html;
								return video;
							default:
								console.error(`Unknown video provider: ${video.ppd_videooptions.videoProvider} `);
								return video;
						}

					}));
				} catch (err) {
					console.error(`Failed to load embed video data: ${err}`)
				}*/
				if (userMediaGallery.length > 0) {
					commit('setGalleryMedia', userMediaGallery);
				}
				commit('updateIsLoadingState', false);
			} else {
				commit('setProfileErrorStatus', userData.error);
				commit('updateIsLoadingState', false);
			}
		},
		async saveProfileLikes({commit}, likeObj) {
			let {likeType, likerGUID, likedContent} = {
				likeType: likeObj.likeType,
				likerGUID: likeObj.likerGUID,
				likedContent: likeObj.likedContent,
			};
			const profileLikesStat = await API_LIKE_PROFILE_OR_PHOTO_2(
				likeType,
				likerGUID,
				likedContent
			);
			commit('updateProfileLikesStatus', {
				stat: profileLikesStat,
				likedContent: likeObj.likedContent,
			});
		},
		async getUserLikes({commit}, userGUID) {
			const userLikes = await API_GET_PROFILE_OR_PHOTO_LIKES(userGUID);
			commit('setUserLikeState', userLikes.data.currentLikes);
		},
		async saveProfileImageLikes({commit}, likeObj) {
			let {likeType, likerGUID, likedContent} = {
				likeType: likeObj.likeType,
				likerGUID: likeObj.likerGUID,
				likedContent: likeObj.likedContent,
			};
			const imageLikesStat = await API_LIKE_PROFILE_OR_PHOTO_2(
				likeType,
				likerGUID,
				likedContent
			);
			commit('updateProfileImageLikesStatus', {
				stat: imageLikesStat,
				content: likeObj.likedContent
			});
		},
	},
};

const communityGalleryInfo = {
	state: {
		gallery: [],
		storedGallery: [],
		featuredMedia: {},
		featuredMediaProfile: {},
		userLikes: [],
		galleryLoaded: false,
		featuredLoaded: false
	},
	mutations: {
		initialCommunityGallery(state, payload) {
			let staffPicks = [],
				filteredGallery = [],
				shuffledGallery = [],
				staffPicksGuids = [];

			for (const media of payload) {
				if (media.ppd_lensmodel === null) {
					media.displayLenses = false;
				}
				if (media.ppd_cameramodel === null) {
					media.displayCamera = false;
				}

				//compare userLikes data to current galleryItem
				media.isLiked = state.userLikes.includes(media.ppd_usermediaid);

				if (media.ppd_photolikes === undefined) {
					media.ppd_photolikes = 0;
				}
				if (!media.ppd_isstaffpick) {
					filteredGallery.push(media);
				} else if (media.ppd_isstaffpick && !staffPicksGuids.includes(media.ppd_usermediaid)) {
					staffPicksGuids.push(media.ppd_usermediaid);
					staffPicks.push(media);
				}
			}
			shuffledGallery = SHUFFLE_ARRAY(filteredGallery);
			if (staffPicks.length) {
				state.gallery = [...state.gallery, ...staffPicks, ...shuffledGallery];
				state.storedGallery = [...state.storedGallery, ...staffPicks, ...filteredGallery];
			} else {
				state.gallery = [...state.gallery, ...shuffledGallery];
				state.storedGallery = [...state.storedGallery, ...filteredGallery];
			}
			state.galleryLoaded = true;
		},
		setFeaturedMedia(state, payload) {
			state.featuredMedia = payload;
		},
		setFeaturedMediaProfile(state, payload) {
			state.featuredMediaProfile = payload;
		},
		setFeaturedLoaded(state) {
			state.featuredLoaded = true;
		},
		galleryReset(state) {
			state.gallery = [];
			state.storedGallery = [];
			state.galleryLoaded = false;
		},
		updateImageLikesStatus(state, payload) {
			const data = payload.stat?.data || {};
			const currentLikes = data.currentLikes ?? [];
			const contentId = payload.content;

			state.userLikes = currentLikes;

			const apply = (item) => {
				if (!item || String(item.ppd_usermediaid) !== String(contentId)) return;
				if (data.newCount != null) {
					Vue.set(item, 'ppd_photolikes', Number(data.newCount));
				}
				Vue.set(item, 'isLiked', currentLikes.some(
					id => String(id) === String(contentId)
				));
			};

			state.gallery.forEach(apply);
			state.storedGallery.forEach(apply);
			apply(state.featuredMedia);
		},
		setImageLikeState(state, payload) {
			state.userLikes = payload;
		},
	},
	actions: {
		async getCommunityGallery({commit, dispatch}, {profileId, page, camera, lens, genre}) {
			if (profileId) {
				await dispatch('getUserImageLikes', profileId);
			}
			const data = await API_GET_COMMUNITY_GALLERY({page, camera, lens, genre});
			commit('initialCommunityGallery', data.gallery);
		},
		async getFeaturedMedia({state, commit, dispatch}, {imageURL}) {
			const setDefaultFeature = async () => {
				commit('setFeaturedMedia', state.storedGallery[0]);
				await dispatch(
					'getFeaturedMediaProfile',
					state.storedGallery[0].ppd_publicprofile.ppd_profileurl
				);
				commit('setFeaturedLoaded');
			};

			let mediaObject;
			if (imageURL.includes('/media/fetch/')) {
				//strip url for the zestyguid of the image to fetch media item from Dynamics
				let zuidRegex = /media\/fetch\/(.*)\?(.*)/;
				let zestyGuid = zuidRegex.exec(imageURL);
				mediaObject = {
					ppd_zestyguid: [zestyGuid[1]],
				};
			} else {
				mediaObject = {
					ppd_imageurl: [imageURL],
				};
			}
			try {
				let imageData = await API_GET_MEDIA_ITEM(mediaObject);
				//If no media is specified or returned populate featured image with first image returned from community gallery api
				if (imageData.data.length === 0) {
					await setDefaultFeature();
				} else {
					commit('setFeaturedMedia', imageData.data[0]);
					commit('setFeaturedLoaded');
					dispatch('getFeaturedMediaProfile', imageData.data[0].ppd_publicprofile.ppd_profileurl);
				}
			} catch {
				await setDefaultFeature();
			}
		},
		async getFeaturedMediaProfile({state, commit}, username) {
			try {
				let userData = await API_GET_LEAN_PROFILE(username);
				let {
					ppd_displayname,
					ppd_flagstatus,
					ppd_profileurl,
					ppd_publicprofileid
				} = userData.data.profile;
				let ppd_imageurl = userData.data.images.filter(image => image.ppd_isprofile)[0].ppd_imageurl;
				commit('setFeaturedMediaProfile', {
					ppd_displayname,
					ppd_flagstatus,
					ppd_profileurl,
					ppd_publicprofileid,
					ppd_imageurl
				});
			} catch {
				commit('setFeaturedMediaProfile', state.storedGallery[0].ppd_publicprofile);
			}
		},
		async saveImageLikes({commit}, likeObj) {
			let {likeType, likerGUID, likedContent} = {
				likeType: likeObj.likeType,
				likerGUID: likeObj.likerGUID,
				likedContent: likeObj.likedContent
			};
			const imageLikesStat = await API_LIKE_PROFILE_OR_PHOTO_2(
				likeType,
				likerGUID,
				likedContent
			);
			commit('updateImageLikesStatus', {stat: imageLikesStat, content: likeObj.likedContent});
		},
		async getUserImageLikes({commit}, userGUID) {
			const imageLikes = await API_GET_PROFILE_OR_PHOTO_LIKES(userGUID);
			commit('setImageLikeState', imageLikes.data.currentLikes);
		}
	}
};
export default new Vuex.Store({
	modules: {
		Account: accountInfo,
		Public: publicUserInfo,
		CommunityGallery: communityGalleryInfo,
	},
	state: {
		ProfileSelf: {
			profile: {},
			usermedia: [],
			pendingUsermedia: [],
			General: {
				saveStatus: 'initial',
				errors: {
					displayName: '',
					profilePhoto: '',
				},
			},
			Location: {
				saveStatus: 'initial',
			},
			MyStory: {
				saveStatus: 'initial',
			},
			WhatIDo: {
				saveStatus: 'initial',
			},
			MySpecialties: {
				saveStatus: 'initial',
			},
			badges: {
				saveStatus: 'saving',
				public: [],
				hidden: [],
			},
			badgesStatical: [],
			SocialLinks: {
				saveStatus: 'initial',
			},
			MySonySystem: {
				saveStatus: 'saving',
			},
		},
		UserGear: [],
		Contact: {},
		moreMembers: {},
		isSaving: false,
		isProfileNew: false,
		showLoginModal: false,
	},
	mutations: {
		/******* Profile State ********/
		createPublicProfile(state) {
			state.Contact.ppd_auprofileprivate = false;
		},
		updateProfileSelfInfo(state, payload) {
			state.ProfileSelf.profile = payload;
		},
		/******* Contact State ********/
		updateContactInfo(state, payload) {
			const mappedPayload = {...payload};
			const photographerTypeMulti = mappedPayload.ppd_photographertype_aumulti;
			if (typeof photographerTypeMulti === 'string' && photographerTypeMulti.trim()) {
				const selectedTypes = photographerTypeMulti
					.split(',')
					.map(item => item.trim().toLowerCase())
					.filter(Boolean);
				mappedPayload.ppd_stillphotography = selectedTypes.includes('photographer');
				mappedPayload.ppd_videography = selectedTypes.includes('videographer');
				mappedPayload.ppd_filmmaker = selectedTypes.includes('filmmaker');
			}
			state.Contact = mappedPayload;
		},
		setContactInfo(state, payload) {
			Object.keys(payload).forEach(function (key, index) {
				Vue.set(state.Contact, key, payload[key]);
			});
		},
		/******* User Media ********/
		replaceProfileSelfUserMedia(state, payload) {
			state.ProfileSelf.usermedia = payload;
		},
		addToProfileSelfUsermedia(state, payload) {
			//takes in an array of user media objects and adds them to usermedia
			payload.forEach(item => {
				state.ProfileSelf.usermedia.push(item);
			});
			state.ProfileSelf.pendingUsermedia = payload;
		},
		updatePendingUsermedia(state, payload) {
			state.ProfileSelf.pendingUsermedia = payload;
		},
		async removeProfileSelfUsermedia(state, payload) {
			// build temp array and filter based on media id
			const newArray = state.ProfileSelf.usermedia.filter(item => item.ppd_usermediaid !== payload.ppd_usermediaid);

			// set state user media to new array
			state.ProfileSelf.usermedia = newArray;
		},
		updateProfileSelfUserMedia(state, payload) {
			//takes in an array of user media objects and updates where mediaIDs match
			payload.forEach(saveditem => {
				state.ProfileSelf.usermedia.forEach((stateItem, index) => {
					if (saveditem.ppd_usermediaid === stateItem.ppd_usermediaid) {
						for (const someKey in saveditem) {
							//update only the patched properties (Vue.set: ppd_altdescription/alt may be new keys)
							Vue.set(stateItem, someKey, saveditem[someKey]);
						}
					}
				});
			});
		},
		updateProfileSelfUserMediaProfileImage(state, payload) {
			let imageType = payload.uploadAs;
			let imageBlob = payload.imageBlob;

			state.ProfileSelf.usermedia.forEach(stateItem => {
				if (imageType === 'profile' && stateItem.ppd_isprofile) {
					stateItem.ppd_imageurl = imageBlob;
				} else if (imageType === 'backdrop' && stateItem.ppd_isbackdrop) {
					stateItem.ppd_imageurl = imageBlob;
				}
			});
		},
		/******* Sony System ********/
		updateUserGear(state, payload) {
			state.UserGear = payload;
			state.ProfileSelf.MySonySystem.saveStatus = 'initial';
		},
		setGearItemPublic(state, payload) {
			state.UserGear[payload.index].ppd_ispublic = payload.value;
			state.UserGear[payload.index].isUpdated = true;
		},
		updateGearItem(state, payload) {
			if (payload.ppd_maincamera != null) {
				//set all cameras main attribute to false
				state.UserGear.forEach(item => {
					if (item.ppd_equipmenttype === 'body') {
						item.ppd_maincamera = false;
					}
				});
				state.UserGear[payload.index].ppd_maincamera =
					payload.ppd_maincamera;
			}
			if (payload.ppd_serialnbr) {
				state.UserGear[payload.index].ppd_serialnbr =
					payload.ppd_serialnbr;
			}
			if (payload.ppd_purchasedate) {
				state.UserGear[payload.index].ppd_purchasedate =
					payload.ppd_purchasedate;
			}
		},
		removeGearItem(state, index) {
			state.UserGear.splice(index, 1);
		},
		/******* Badges ********/
		updateProfileSelfBadges(state, payload) {
			let badges = state.ProfileSelf.badges;
			let tempPublic = [];
			let tempHidden = [];

			if (payload) {
				payload.forEach(badge => {
					let badgeData = {
						ppd_badgeearnedid: badge.ppd_badgeearnedid,
						ppd_featured: badge.ppd_featured,
						ppd_occurences: badge.ppd_occurrences,
						...badge.ppd_badgeid,
					};
					if (badgeData.ppd_badgeid === '9e11f920-d5be-e911-a85b-000d3a372932' ||
						badgeData.ppd_badgeid === 'faa457d0-d6be-e911-a85b-000d3a372932') {
						badgeData.fixed = true;
					}
					if (badgeData.ppd_featured || badgeData.fixed) {
						tempPublic.push(badgeData);
					} else {
						tempHidden.push(badgeData);
					}
				});
			}
			badges.public = tempPublic;
			badges.hidden = tempHidden;
			badges.saveStatus = 'initial';
		},
		updateProfileSelfBadgesStatical(state, payload) {
			state.ProfileSelf.badgesStatical = payload;
		},
		updateHiddenBadges(state, payload) {
			let badges = state.ProfileSelf.badges;
			let updatedBadge;
			payload.forEach(badge => {
				if (badge.ppd_featured) {
					updatedBadge = badge;
				}
			});
			if (updatedBadge) {
				if (badges.public) {
					badges.public = badges.public.filter(
						badgeEarned => badgeEarned.ppd_badgeearnedid !== updatedBadge.ppd_badgeearnedid
					);
				}
				updatedBadge.ppd_featured = false;
				updatedBadge.isUpdated = true;
			}
			state.ProfileSelf.badges.hidden = payload;
		},
		updateDisplayBadges(state, payload) {
			let badges = state.ProfileSelf.badges;
			let updatedBadge;
			payload.forEach(badge => {
				if (!badge.ppd_featured) {
					updatedBadge = badge;
				}
			});
			if (updatedBadge) {
				if (badges.hidden) {
					badges.hidden = badges.hidden.filter(
						badgeEarned => badgeEarned.ppd_badgeearnedid !== updatedBadge.ppd_badgeearnedid
					);
				}
				updatedBadge.ppd_featured = true;
				updatedBadge.isUpdated = true;
			}
			state.ProfileSelf.badges.public = payload;
		},
		updateProfileSelfIsPublic(state, payload) {
			state.ProfileSelf.profile.ppd_ispublic = payload;
			// if (!payload) {
			// 	//Disable section card visibility
			// 	state.ProfileSelf.profile.ppd_bioispublic = false;
			// 	state.ProfileSelf.profile.ppd_whatidoispublic = false;
			// 	state.ProfileSelf.profile.ppd_locationispublic = false;
			// 	state.ProfileSelf.profile.ppd_photographerspecialty_ispublic = false;
			// 	state.ProfileSelf.profile.ppd_sociallinksispublic = false;
			// 	state.ProfileSelf.profile.ppd_badgeispublic = false;
			// 	state.ProfileSelf.profile.ppd_galleryispublic = false;
			// 	state.ProfileSelf.profile.ppd_gearispublic = false;
			// 	//Disable link visibility
			// 	state.ProfileSelf.profile.ppd_facebookurlispublic = false;
			// 	state.ProfileSelf.profile.ppd_instagramurlispublic = false;
			// 	state.ProfileSelf.profile.ppd_twitterurlispublic = false;
			// 	state.ProfileSelf.profile.ppd_websiteurlispublic = false;
			// 	state.ProfileSelf.profile.ppd_youtubeurlispublic = false;
			// } else {
			// 	state.ProfileSelf.profile.ppd_facebookurlispublic = true;
			// 	state.ProfileSelf.profile.ppd_instagramurlispublic = true;
			// 	state.ProfileSelf.profile.ppd_twitterurlispublic = true;
			// 	state.ProfileSelf.profile.ppd_websiteurlispublic = true;
			// 	state.ProfileSelf.profile.ppd_youtubeurlispublic = true;
			// }
		},
		updateProfileNewStatus(state, isNew) {
			state.isProfileNew = isNew;
		},
		updatePublicProfileField(state, payload) {
			//Needs refactored to perform more like setContactInfo mutation
			state.ProfileSelf.profile[payload.key] = payload.value;
		},
		updateSectionSaveStatus(state, payload) {
			let section = state.ProfileSelf[payload.sectionName];
			section['saveStatus'] = payload.saveStatus;
		},
		updateSectionErrors(state, payload) {
			let section = state.ProfileSelf[payload.sectionName];
			section['errors'][payload.error.field] = payload.error.message;
		},
		updateMoreMembers(state, payload) {
			state.moreMembers = payload;
		},
		isLoading(state, payload) {
			state.isSaving = payload;
		},
		updateLoginModalVisibility(state, payload) {
			state.showLoginModal = payload;
		}
	},
	getters: {
		profileSelfUserMedia: state => {
			return state.ProfileSelf.usermedia;
		},
		profileSelfInfo: state => {
			return state.ProfileSelf.profile;
		},
		contact: state => {
			return state.Contact;
		},
		isGearPublic: state => index => {
			return state.UserGear[index].ppd_ispublic;
		},
		userDisplayName: state => {
			return state.ProfileSelf.profile.ppd_displayname;
		},
	},
	actions: {
		togglePublicProfileVisibility({commit, state}, isVisible) {
			let profileState = {
				ppd_ispublic: isVisible,
			};
			// if (!isVisible) {
			// 	profileState.ppd_bioispublic = false;
			// 	profileState.ppd_whatidoispublic = false;
			// 	profileState.ppd_locationispublic = false;
			// 	profileState.ppd_photographerspecialty_ispublic = false;
			// 	profileState.ppd_sociallinksispublic = false;
			// 	profileState.ppd_badgeispublic = false;
			// 	profileState.ppd_galleryispublic = false;
			// 	profileState.ppd_gearispublic = false;
			// 	//Disable link visibility
			// 	profileState.ppd_facebookurlispublic = false;
			// 	profileState.ppd_instagramurlispublic = false;
			// 	profileState.ppd_twitterurlispublic = false;
			// 	profileState.ppd_websiteurlispublic = false;
			// 	profileState.ppd_youtubeurlispublic = false;
			// } else {
			// 	profileState.ppd_facebookurlispublic = true;
			// 	profileState.ppd_instagramurlispublic = true;
			// 	profileState.ppd_twitterurlispublic = true;
			// 	profileState.ppd_websiteurlispublic = true;
			// 	profileState.ppd_youtubeurlispublic = true;
			// }
			commit('isLoading', true);
			commit('updateProfileSelfIsPublic', isVisible);
			API_PATCH_PUBLIC_PROFILE_SELF(
				$cookies.get('au_guid'),
				$cookies.get('au_profile_guid'),
				profileState
			)
				.then(response => commit('isLoading', false))
				.catch(error => {
					commit('isLoading', false);
					const requestError = `TOGGLE PUBLIC PROFILE VISIBILITY ERROR: ${error}`;
					console.error(requestError);
				});
		},
		async saveSectionInfo({commit, dispatch}, sectionFields) {
			if (sectionFields) {
				let promises = [];
				let contactFormData = new FormData();
				let sectionName = sectionFields.sectionName;
				commit({
					type: 'updateSectionSaveStatus',
					sectionName: sectionName,
					saveStatus: 'saving',
				});
				if (sectionFields.publicKey) {
					await dispatch(
						'saveSectionVisibility',
						sectionFields.publicKey
					);
				}
				if (sectionFields.contact) {
					for (const key in sectionFields.contact) {
						contactFormData.append(key, sectionFields.contact[key]);
					}
					promises.push(
						API_PATCH_CONTACT(
							$cookies.get('au_guid'),
							contactFormData
						)
					);
				}
				if (sectionFields.public) {
					promises.push(
						API_PATCH_PUBLIC_PROFILE_SELF(
							$cookies.get('au_guid'),
							$cookies.get('au_profile_guid'),
							sectionFields.public
						)
					);
				}
				await Promise.all(
					promises.map(promise => promise.catch(error => error))
				);
				if (sectionName !== 'General') {
					commit({
						type: 'updateSectionSaveStatus',
						sectionName: sectionName,
						saveStatus: 'complete',
					});
				}
			}
		},
		async saveSectionVisibility({state}, sectionVisibleKey) {
			API_PATCH_PUBLIC_PROFILE_SELF(
				$cookies.get('au_guid'),
				$cookies.get('au_profile_guid'),
				{
					[sectionVisibleKey]:
						state.ProfileSelf.profile[sectionVisibleKey],
				}
			);
		},
		async savePublicContactInfo({state}) {
			let contactFormData = new FormData();
			let contactInfoUpdates = (({
										   address3_stateorprovince,
										   address3_country,
										   ppd_about,
										   ppd_photographertype_aumulti,
										   ppd_photographerspecialty_aumulti,
										   ppd_instagramurl,
										   ppd_facebookurl,
										   ppd_twitterurl,
										   ppd_youtubeurl,
										   ppd_ticktokurl,
										   ppd_metathreadsurl,
										   websiteurl,
									   }) => ({
				address3_stateorprovince,
				address3_country,
				ppd_about,
				ppd_photographertype_aumulti,
				ppd_photographerspecialty_aumulti,
				ppd_instagramurl,
				ppd_facebookurl,
				ppd_twitterurl,
				ppd_youtubeurl,
				ppd_ticktokurl,
				ppd_metathreadsurl,
				websiteurl,
			}))(state.Contact);
			for (let key in contactInfoUpdates) {
				if (
					contactInfoUpdates[key] &&
					contactInfoUpdates[key] !== 'null'
				) {
					contactFormData.append(key, contactInfoUpdates[key]);
				} else {
					contactFormData.append(key, '');
				}
			}
			API_PATCH_CONTACT($cookies.get('au_guid'), contactFormData);
		},
		async savePublicProfileInfo({state}) {
			API_PATCH_PUBLIC_PROFILE_SELF(
				$cookies.get('au_guid'),
				$cookies.get('au_profile_guid'),
				state.ProfileSelf.profile
			);
		},
		async saveBadges({dispatch, commit, state}) {
			let sectionName = 'badges';
			commit({
				type: 'updateSectionSaveStatus',
				sectionName: sectionName,
				saveStatus: 'saving',
			});
			await dispatch('saveSectionVisibility', 'ppd_badgeispublic');
			let allBadges = state.ProfileSelf.badges.hidden.concat(
				state.ProfileSelf.badges.public
			);
			await API_PATCH_USER_BADGES(
				$cookies.get('au_profile_guid'),
				allBadges
			);
			commit({
				type: 'updateSectionSaveStatus',
				sectionName: sectionName,
				saveStatus: 'complete',
			});
		},
		async saveSonySystem({dispatch, commit, state}) {
			let sectionName = 'MySonySystem';
			commit({
				type: 'updateSectionSaveStatus',
				sectionName: sectionName,
				saveStatus: 'saving',
			});
			await dispatch('saveSectionVisibility', 'ppd_gearispublic');
			await Promise.all(
				state.UserGear.map(async gearItem => {
					if (gearItem.isUpdated) {
						let updatedGearItem = {
							ppd_ispublic: gearItem.ppd_ispublic,
						};
						API_PATCH_USER_GEAR(
							gearItem.ppd_gear_lockerid,
							updatedGearItem,
							$cookies.get('au_guid')
						);
					}
				})
			).then(() => {
				commit({
					type: 'updateSectionSaveStatus',
					sectionName: sectionName,
					saveStatus: 'complete',
				});
			});
		},
		async saveGearLockerItem({dispatch, commit, state}, gearIndex) {
			//Set current gear item based on current state
			let gearItem = state.UserGear[gearIndex];
			//Object destructuring to get all updated values
			let gearItemUpdates = (({
										ppd_serialnbr,
										ppd_maincamera,
										ppd_purchasedate,
									}) => ({
				ppd_serialnbr,
				ppd_maincamera,
				ppd_purchasedate,
			}))(gearItem);
			await API_PATCH_USER_GEAR(
				gearItem.ppd_gear_lockerid,
				gearItemUpdates,
				$cookies.get('au_guid')
			);
		},
		async createGearLockerItem({commit, state}, gearObject) {
			try {
				await API_CREATE_USER_GEAR($cookies.get('au_guid'), gearObject);
			} catch (err) {
				console.log(err);
			}
			let updatedGear = await API_GET_USER_GEAR($cookies.get('au_guid'));

			commit('updateUserGear', updatedGear.value);
		},
		async deleteGearLockerItem({state, commit}, gearIndex) {
			let gearItem = state.UserGear[gearIndex];
			if (!gearItem) {
				return;
			}
			const gearLockerId = gearItem.ppd_gear_lockerid;
			await API_DELETE_USER_GEAR(gearLockerId, $cookies.get('au_guid'));
			const currentIndex = state.UserGear.findIndex(
				item => item.ppd_gear_lockerid === gearLockerId
			);
			if (currentIndex > -1) {
				commit('removeGearItem', currentIndex);
			}
		},
		async getMoreMembers({commit}, payload) {
			const moreMembers = await API_GET_MORE_MEMBERS(payload);
			commit('updateMoreMembers', moreMembers);
		},
		async uploadProfilePhoto({commit}, photoData) {
			const {media} = await uploadPhoto(photoData.media.fileToUpload, 'profile');
			// callers expect the old 'uploaded successfully' array shape
			return media ? [media] : [];
		},
		async enablePublicProfile({}, enableFields) {
			const enabledProfile = await API_ENABLE_PUBLIC_PROFILE_SELF(
				$cookies.get('au_guid'),
				enableFields
			);
			if (enabledProfile) {
				return enabledProfile.data;
			}
		},
		async reportProfileOrImage({}, data) {
			const reportedItem = await API_REPORT_PROFILE_OR_PHOTO(
				$cookies.get('au_guid'),
				data
			);
			if (reportedItem) {
				console.log(reportedItem);
				return reportedItem.data;
			} else if (error) {
				return error.status;
			}
		},
	},
});
